import { TUI_CONFIRM } from '@taiga-ui/kit';
import { AccountService } from '../account/account.service';
import { OutkeepPermissionService } from '../permission/outkeep-permission.service';
import { XAlertService } from '../x-alert.service';
import { RequestsApiService } from './requests-api.service';
import { RequestService } from './request.service';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import {
  EMPTY,
  forkJoin,
  map,
  Observable,
  of,
  repeat,
  shareReplay,
  Subject,
  switchMap,
  tap,
  zip,
} from 'rxjs';
import {
  CreateRequestDealDetailsData,
  RequestDealDetailsExtended,
  RequestDealDetailsInstallingEnv,
  RequestDealDetailsServiceTranslations,
  RequestDealDetailsServiceTranslationsPayload,
  RequestDealDetailsWithInstaller,
  RequestDTO,
} from '@core/types';
import { RequestModel } from '@core/models';
import { TuiDialogService } from '@taiga-ui/core';
import { catchError } from 'rxjs/operators';
import { RequestStatusDealDetailsForm } from '@features/requests/request-form/components/request-form-status/types';
import { isPersonPopulated } from '@core/helpers';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class RequestDealDetailsService {
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly requestService = inject(RequestService);
  private readonly accountService = inject(AccountService);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly permissionService = inject(OutkeepPermissionService);
  private readonly alertService$ = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private currentDealDetailsFormData: RequestStatusDealDetailsForm | null =
    null;

  get currentDealDetailsForm(): RequestStatusDealDetailsForm | null {
    return this.currentDealDetailsFormData;
  }

  set currentDealDetailsForm(
    currentDealDetailsForm: RequestStatusDealDetailsForm | null,
  ) {
    this.currentDealDetailsFormData = currentDealDetailsForm;
  }

  private translates: RequestDealDetailsServiceTranslationsPayload = {
    [RequestDealDetailsServiceTranslations.IsCurrentUserInstallerInRequestsWithoutDealDetails]:
      'Некоторые выбранные заявки не имеют описаний сделок, а создавать их может только СЛС заявки. Вы хотите снять выделение с недоступных заявок и продолжить редактирование описаний сделок?',
    [RequestDealDetailsServiceTranslations.ErrorNoSelectedDealDetailsLeft]:
      'Не осталось выделенных заявок, модальное окно групповых действий закрыто',
    [RequestDealDetailsServiceTranslations.ErrorNoPermissionsToEditAllDealDetails]:
      'Некоторые выбранные заявки имеют детали сделки, но для их редактирования у вас не хватает прав. Снять выделение с недоступных для редактирования заявок и продолжить работу с деталями сделок?',
    [RequestDealDetailsServiceTranslations.ErrorSavingDealDetails]:
      'Ошибка сохранения данных деталей сделки',
    [RequestDealDetailsServiceTranslations.Error]: 'Ошибка',
    [RequestDealDetailsServiceTranslations.ConfirmDialogYes]: 'Да',
    [RequestDealDetailsServiceTranslations.ConfirmDialogNo]: 'Нет',
  };

  constructor() {
    this.outkeepTranslateService
      .translate(Object.values(RequestDealDetailsServiceTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  private $selectedRequests = this.requestService.selectedRequests;
  private $selectedRequestsDealDetailsByRequestId = signal<
    Record<RequestDTO['id'], RequestDealDetailsWithInstaller>
  >([]);
  private $requestsWithDealDetails = signal<RequestModel[]>([]);
  private $requestsWithoutDealDetails = signal<RequestModel[]>([]);

  private readonly $user = this.accountService.$currentUser;

  public requestDealDetails$ = this.requestService.requestId$.pipe(
    switchMap(id =>
      this.requestsApiService
        .getRequestDealDetails$(
          id,
          this.requestService.$request().installerId?.id ?? null,
        )
        .pipe(
          repeat({ delay: () => this.reload$ }),
          catchError(() => of(null)),
        ),
    ),
    shareReplay(1),
  );

  private readonly reload$ = new Subject<void>();

  public readonly $requestDealDetails =
    toSignal<RequestDealDetailsExtended | null>(this.requestDealDetails$);

  public reloadDealDetails$() {
    this.reload$.next();
  }

  getDetailsForSelectedRequests$(viewObserver: Subject<never>) {
    return zip(
      this.requestsApiService.getRequestsDealDetails(
        this.$selectedRequests().map(({ id }) => id),
      ),
      this.permissionService.canEditDealDetails$,
    ).pipe(
      switchMap(([dealDetails, canEditDealDetails]) => {
        this.$selectedRequestsDealDetailsByRequestId.set(
          dealDetails.reduce(
            (acc, details) => {
              acc[details.requestId] = details;

              return acc;
            },
            {} as Record<number, RequestDealDetailsWithInstaller>,
          ),
        );

        if (this.$selectedRequests().length !== dealDetails.length) {
          // не у всех заявок есть детали сделки
          return this.notAllRequestsHaveDealDetails$(viewObserver).pipe(
            switchMap(() => of(canEditDealDetails)),
          );
        }

        return of(canEditDealDetails);
      }),
      switchMap(canEditDealDetails => {
        if (!canEditDealDetails) {
          return this.clearDealDetailsSelectionIfNoPermissions$(viewObserver);
        }

        return of(true);
      }),
    );
  }

  private notAllRequestsHaveDealDetails$(
    viewObserver: Subject<never>,
  ): Observable<boolean> {
    const selectedRequests = this.$selectedRequests();
    const requestsWithoutDealDetails: RequestModel[] = [];
    const requestsWithDealDetails: RequestModel[] = [];

    selectedRequests.forEach(request => {
      if (this.$selectedRequestsDealDetailsByRequestId()[request.id]) {
        requestsWithDealDetails.push(request);
      } else {
        requestsWithoutDealDetails.push(request);
      }
    });
    this.$requestsWithDealDetails.set(requestsWithDealDetails);
    this.$requestsWithoutDealDetails.set(requestsWithoutDealDetails);

    const isCurrentUserInstallerInRequestsWithoutDealDetails =
      requestsWithoutDealDetails.every(request => {
        const installerId = request.installerId?.id || request.installerId;
        const personId = this.$user()?.personId;

        if (!installerId || !personId) {
          return false;
        }

        const installerIdValue = isPersonPopulated(installerId)
          ? installerId.id
          : installerId;
        const personIdValue = isPersonPopulated(personId)
          ? personId.id
          : personId;

        return +installerIdValue === +personIdValue;
      });

    if (!isCurrentUserInstallerInRequestsWithoutDealDetails) {
      // Если текущий пользователь не установщик в заявках без деталей сделок,
      // то предлагаем ему снять выделение с недоступных заявок
      return this.openConfirmDialog$(
        this.translates[
          RequestDealDetailsServiceTranslations
            .IsCurrentUserInstallerInRequestsWithoutDealDetails
        ],
      ).pipe(
        switchMap(clearSomeSelection => {
          if (!clearSomeSelection) {
            viewObserver.complete();

            return EMPTY;
          }

          if (!requestsWithDealDetails.length) {
            // Если все выбранные заявки не имеют деталей сделок,
            // то снимем выделение со всех заявок
            this.alertService$.warn(
              this.translates[
                RequestDealDetailsServiceTranslations
                  .ErrorNoSelectedDealDetailsLeft
              ],
              '',
              5000,
            );
            this.requestService.unselectAllSelectedRequestsIds();
            viewObserver.complete();

            return EMPTY;
          }

          this.requestService.unselectFromSelectedRequestsIds(
            requestsWithoutDealDetails,
          );

          return of(true);
        }),
      );
    }

    return of(true);
  }

  private clearDealDetailsSelectionIfNoPermissions$(
    viewObserver: Subject<never>,
  ): Observable<boolean> {
    return this.openConfirmDialog$(
      this.translates[
        RequestDealDetailsServiceTranslations
          .ErrorNoPermissionsToEditAllDealDetails
      ],
    ).pipe(
      switchMap(clearSomeSelection => {
        if (!clearSomeSelection) {
          viewObserver.complete();

          return EMPTY;
        }

        this.requestService.unselectFromSelectedRequestsIds(
          this.$requestsWithDealDetails(),
        );

        if (
          !this.$requestsWithoutDealDetails().length ||
          !this.$selectedRequests().length
        ) {
          // Если все выбранные заявки имеют детали сделок или не осталось выбранных заявок,
          this.alertService$.warn(
            this.translates[
              RequestDealDetailsServiceTranslations
                .ErrorNoSelectedDealDetailsLeft
            ],
            '',
            5000,
          );
          this.requestService.unselectAllSelectedRequestsIds();
          viewObserver.complete();

          return EMPTY;
        }

        return of(true);
      }),
    );
  }

  createRequestDealDetails$(
    formData: RequestStatusDealDetailsForm,
    isGroupAction = false,
    isInsideWorkflow = false,
  ): Observable<null> {
    const {
      thirdParties,
      problems,
      nothingGiven,
      documentsGiven,
      keysGiven,
      primaryDocumentation,
    } = formData;
    let requestIds: number[];
    const installerTime = formData.installerTime as string;
    const subjTime = formData.subjTime as string;
    const releaseStartTime = formData.releaseStartTime as string;
    const completedTime = formData.completedTime as string;
    const demountedDevicesCount = formData.demountedDevicesCount;
    const installingEnv =
      formData.installingEnv as RequestDealDetailsInstallingEnv;
    let durationInMinutes: number | undefined;

    if (isGroupAction) {
      const dealDuration =
        (+completedTime - +releaseStartTime) / this.$selectedRequests().length;

      durationInMinutes = Math.round(dealDuration / 1000 / 60);

      requestIds = this.$selectedRequests().map(({ id }) => id);
    } else {
      requestIds = [this.requestService.$request().id];
    }

    const payload: CreateRequestDealDetailsData = {
      installerTime,
      subjTime,
      releaseStartTime,
      completedTime,
      installingEnv,
      thirdParties,
      problems: problems || '',
      nothingGiven,
      documentsGiven,
      demountedDevicesCount,
      keysGiven,
      primaryDocumentation,
      ...(durationInMinutes ? { durationInMinutes } : {}),
      ...(isGroupAction
        ? {}
        : { releaseTime: this.requestService.$request().releaseTime }),
    };

    return forkJoin(
      requestIds.map(requestId =>
        this.requestsApiService.createRequestDealDetails$(requestId, payload),
      ),
    ).pipe(
      catchError(err => {
        this.alertService$.error(
          this.translates[RequestDealDetailsServiceTranslations.Error],
          `${
            this.translates[
              RequestDealDetailsServiceTranslations.ErrorSavingDealDetails
            ]
          }! ${getDealDetailsErrorMessage(err)}`,
          10000,
        );

        if (isInsideWorkflow) {
          throw err;
        }

        return of(null);
      }),
      tap(() => {
        if (isGroupAction) {
          this.requestService.reloadSelectedRequests$();
        } else {
          this.requestService.reloadRequest();
          this.reloadDealDetails$();
        }
      }),
      map(() => null),
    );
  }

  updateRequestDealDetails$(
    formData: RequestStatusDealDetailsForm,
    groupAction = false,
    isInsideWorkflow = false,
  ): Observable<null> {
    return this.createRequestDealDetails$(
      formData,
      groupAction,
      isInsideWorkflow,
    );
  }

  openConfirmDialog$(content: string): Observable<boolean> {
    return this.dialogService$.open<boolean>(TUI_CONFIRM, {
      size: 's',
      closable: false,
      dismissible: false,
      data: {
        content,
        yes: this.translates[
          RequestDealDetailsServiceTranslations.ConfirmDialogYes
        ],
        no: this.translates[
          RequestDealDetailsServiceTranslations.ConfirmDialogNo
        ],
      },
    });
  }
}

/**
 * Текст ошибки сохранения деталей сделки: сообщение сервера из тела ответа,
 * а не сам объект ответа (иначе в алерте «[object Object]»)
 */
export function getDealDetailsErrorMessage(err: unknown): string {
  const httpError = err as { error?: unknown; message?: unknown } | null;
  const body = httpError?.error;

  if (typeof body === 'string') {
    return body;
  }

  if (typeof body === 'object' && body !== null) {
    const message = (body as { message?: unknown }).message;

    if (typeof message === 'string' && message) {
      return message;
    }
  }

  return typeof httpError?.message === 'string' ? httpError.message : '';
}
